<script setup lang="ts">
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import { faArrowLeft, faPenNib, faXmark } from '@fortawesome/pro-light-svg-icons'
import { extractReference, getNsItemNumber } from '~/utils/solr/law-utils'

const props = defineProps<{
  context: AsideContext
  comment: LawAndCommentary
  law?: { shortTitleWithoutAbbreviation?: string | null } | null
}>()

const emit = defineEmits<{
  close: []
}>()

const { back } = useLawView()

// Save the last updated date when the node type is `legal_commentary`
// so child nodes of type `legal_commentary_section` can access it
const lastUpdated = computed(() => {
  return props.comment?.nodeType === 'legal_commentary' ? props.comment?.lastUpdated ?? null : null
})

const isStandardText = useIsStandardText()
const { textLabelLower } = useLawTextLabels()

const activeCommentaryAriaLabel = 'Kommentar'

const backToTextLabel = computed(() => `Tilbake til ${textLabelLower.value}`)

const contextLabel = computed(() => {
  switch (props.context) {
    case 'commentary': {
      if (isStandardText.value) {
        const fragmentLeaf = props.comment?.lawFragmentId?.split('/').pop()?.toLowerCase() ?? ''
        const nsNumber = getNsItemNumber(props.comment?.lawFragmentId)
        if (nsNumber && fragmentLeaf.startsWith('punkt-')) {
          return `Kommentar til punkt ${nsNumber}`
        }
        return 'Kommentar'
      }
      const suffix = extractReference(props.comment?.title) || props.law?.shortTitleWithoutAbbreviation?.toLowerCase()
      return suffix ? `Kommentar til ${suffix}` : 'Kommentar'
    }
    case 'curated':
      return 'Utvalgte kilder'
    case 'literature':
      return 'Relatert litteratur'
    default:
      return isStandardText.value ? 'Kommentar' : 'Lovkommentar'
  }
})

const authors = computed(() => {
  const names = props.comment?.authors ?? []
  const slugs = props.comment?.authorSlug ?? []

  if (import.meta.dev && slugs.length > 0 && slugs.length !== names.length) {
    console.warn(`[AsideHeader] authorSlug/authors length mismatch: ${slugs.length} vs ${names.length}`, props.comment?.id)
  }

  return names.map((name, i) => ({
    name,
    slug: slugs[i] || name.replace(/\s+/g, '-').toLowerCase(),
  }))
})

function handleClose() {
  emit('close')
}
</script>

<template>
  <section :aria-label="context === 'commentary' ? activeCommentaryAriaLabel : contextLabel" class="sticky top-0 z-[9] bg-surface-elevated flex flex-col gap-m nav:gap-2xs">
    <div class="flex nav:hidden items-center gap-2xs">
      <ControlButton
        size="small"
        :icon="faArrowLeft"
        :aria-label="backToTextLabel"
        class="nav:hidden self-start gap-3xs bg-transparent hover:bg-transparent !p-2xs dark:hover:bg-surface-secondary border rounded-lg border-blurple-500 dark:border-carbon-50 dark:bg-carbon-500"
        @click="back"
      />
      <span class="text-xsmall dark:text-carbon-50" aria-hidden="true">{{ backToTextLabel }}</span>
    </div>
    <div class="flex flex-col md:flex-row justify-between xl:items-center gap-2xs xl:gap-m px-m">
      <h2 class="text-small text-primary font-bold no-underline dark:text-white">
        {{ contextLabel }}
      </h2>
      <Chip v-if="context === 'commentary' && (comment.lastUpdated || lastUpdated)" variant="filled" color="moss" size="small" class="font-bold text-caption hover:outline-transparent shrink-0">
        Bekreftet à jour {{ formatDate(new Date(comment.lastUpdated ?? lastUpdated ?? ''), { year: 'numeric', month: 'long', day: 'numeric' }) }}
      </Chip>
      <ControlButton v-if="context === 'curated' || context === 'literature'" size="small" :icon="faXmark" class="hidden nav:flex p-2xs shadow-sm rounded-lg bg-white dark:bg-surface-primary dark:hover:bg-surface-secondary dark:border-carbon-50 [&>svg]:fill-blurple-500 dark:[&>svg]:fill-carbon-200 [&>svg]:!size-5 hover:bg-transparent" @click="handleClose" />
    </div>
    <div v-if="context === 'commentary' && authors.length" class="flex items-center gap-3xs flex-wrap pb-xs px-m">
      <Icon :icon="faPenNib" class="text-secondary dark:text-carbon-50" size="sm" />
      <template v-for="author in authors" :key="author.slug">
        <NuxtLink :to="{ name: 'author', params: { slug: author.slug } }" class="flex text-caption underline text-secondary dark:text-carbon-50 after:content-[','] last:after:content-['']">
          {{ author.name }}
        </NuxtLink>
      </template>
    </div>
  </section>
</template>
